import { useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import type { UseFormReturn } from 'react-hook-form';
import { ArrowRight, Warehouse } from 'lucide-react';
import type { ICarrier, IFrequentDestination, ILocation } from '@carrierbridge/shared';
import { DEPARTMENTS, COUNTRIES } from '@/lib/geo-data';
import { useCountryDepartments } from '@/hooks/useTariffZones';
import { useFrequentDestinations, useLocations } from '@/hooks/useLocations';
import { useMe, useUpdateProfile } from '@/hooks/useAuth';
import { useToast } from '@/components/ui/Toast';
import { LocationPicker } from './LocationPicker';
import { Select } from '@/components/ui/Select';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import type { QuoteFormValues } from './schema';

type RouteMode = 'direct' | 'from_site' | 'to_site';

/** Département FR dérivé d'un code postal (Corse 2A/2B, DOM 97x approximés). */
function departementDepuisCP(cp?: string | null): string | null {
  const digits = (cp ?? '').replace(/\D/g, '');
  if (digits.length < 2) return null;
  const p2 = digits.slice(0, 2);
  if (p2 === '20') return digits.startsWith('200') || digits.startsWith('201') ? '2A' : '2B';
  if (p2 === '97' || p2 === '98') return digits.slice(0, 3);
  return p2;
}

/** Mode toggle + origin → destination pickers (direct or via a logistics site). */
export function QuoteRouteSection({
  t,
  form,
  routeMode,
  onSelectMode,
  logisticsSites,
  logisticsSiteOptions,
  selectedSite,
  selectedSiteId,
  setSelectedSiteId,
}: {
  t: TFunction;
  form: UseFormReturn<QuoteFormValues>;
  routeMode: RouteMode;
  onSelectMode: (mode: RouteMode) => void;
  logisticsSites: ICarrier[];
  logisticsSiteOptions: { value: string; label: string }[];
  selectedSite: ICarrier | null;
  selectedSiteId: string | null;
  setSelectedSiteId: (id: string | null) => void;
}) {
  const { setValue } = form;
  const { data: locations = [] } = useLocations();
  const { data: me } = useMe();
  const updateProfile = useUpdateProfile();
  const { toast } = useToast();

  const prefs = me?.preferences ?? {};
  /**
   * Le lieu choisi de chaque côté. L'état vit ici et non dans chaque extrémité :
   * les destinations fréquentes se calculent DEPUIS l'origine, il faut donc que
   * le côté destination sache ce que le côté origine a retenu.
   */
  const [originId, setOriginId] = useState<string | null>(null);
  const [destId, setDestId] = useState<string | null>(null);
  const { data: frequent = [] } = useFrequentDestinations(originId);

  /**
   * Applique le défaut de l'utilisateur au premier chargement — et une seule
   * fois. Sans ce verrou, revenir à un formulaire vidé exprès le verrait se
   * re-remplir tout seul, ce qui donne l'impression que le champ refuse
   * d'être effacé.
   */
  const defaultsApplied = useRef(false);
  useEffect(() => {
    if (defaultsApplied.current || locations.length === 0 || !me) return;
    defaultsApplied.current = true;
    const origin = prefs.quoteDefaultOriginLocationId
      ? locations.find((l) => l.id === prefs.quoteDefaultOriginLocationId)
      : undefined;
    const dest = prefs.quoteDefaultDestLocationId
      ? locations.find((l) => l.id === prefs.quoteDefaultDestLocationId)
      : undefined;
    if (origin) {
      setOriginId(origin.id);
      prefill(origin, 'origin');
    }
    if (dest) {
      setDestId(dest.id);
      prefill(dest, 'dest');
    }
    // Volontairement lié au seul chargement des lieux et de l'utilisateur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, me]);

  /** Enregistre (ou retire) un défaut, sans écraser celui de l'autre côté. */
  function saveDefault(side: 'origin' | 'dest', locationId: string | null) {
    const key = side === 'origin'
      ? ('quoteDefaultOriginLocationId' as const)
      : ('quoteDefaultDestLocationId' as const);
    updateProfile.mutate(
      { preferences: { [key]: locationId } },
      {
        onSuccess: () =>
          toast('success', locationId ? t('quote.defaults.saved') : t('quote.defaults.removed')),
        onError: () => toast('error', t('common.error')),
      },
    );
  }

  function selectLocation(loc: ILocation | null, side: 'origin' | 'dest') {
    if (side === 'origin') setOriginId(loc?.id ?? null);
    else setDestId(loc?.id ?? null);
    if (loc) prefill(loc, side);
  }

  function prefill(loc: ILocation, side: 'origin' | 'dest') {
    // `lieu` peut être vide sur une fiche créée sans adresse : sans cette
    // garde, le clic plantait en silence et la sélection semblait ignorée.
    const cp = loc.lieu?.codePostal ?? '';
    const pays = loc.lieu?.pays || 'FRA';
    const dep = departementDepuisCP(cp);
    const depValide = !!dep && DEPARTMENTS.some((d) => d.value === dep);
    if (side === 'origin') {
      setValue('originCountry', pays);
      if (cp) setValue('originPostalCode', cp);
      if (depValide) setValue('originDepartment', dep as string, { shouldValidate: true });
    } else {
      setValue('destCountry', pays);
      if (cp) setValue('destPostalCode', cp);
      if (depValide) setValue('destDepartment', dep as string, { shouldValidate: true });
    }
  }

  return (
    <>
      {/* ── Mode toggle ── */}
      {logisticsSites.length > 0 && (
        <div>
          <p className="mb-1.5 text-[13px] font-medium text-text">{t('quote.mode')}</p>
          <SegmentedControl
            aria-label={t('quote.mode')}
            value={routeMode}
            onChange={(v) => onSelectMode(v as RouteMode)}
            options={[
              { value: 'direct', label: t('quote.modeDirect') },
              { value: 'from_site', label: t('quote.modeFromSite') },
              { value: 'to_site', label: t('quote.modeToSite') },
            ]}
          />
        </div>
      )}

      {/* ── Route: Origin → Destination ── */}
      <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[1fr_24px_1fr] sm:gap-0">
        {/* Origin (or site select) */}
        <EndpointFields
          t={t}
          side="origin"
          form={form}
          isSiteMode={routeMode === 'from_site'}
          logisticsSiteOptions={logisticsSiteOptions}
          selectedSite={selectedSite}
          selectedSiteId={selectedSiteId}
          setSelectedSiteId={setSelectedSiteId}
          locations={locations}
          selectedLocationId={originId}
          onSelectLocation={(loc) => selectLocation(loc, 'origin')}
          frequent={undefined}
          defaultLocationId={prefs.quoteDefaultOriginLocationId ?? null}
          onSetDefault={(id) => saveDefault('origin', id)}
        />

        {/* Arrow */}
        <div className="flex items-center justify-center sm:pt-8">
          <ArrowRight className="h-4 w-4 rotate-90 text-muted sm:rotate-0" aria-hidden="true" />
        </div>

        {/* Destination (or site select) */}
        <EndpointFields
          t={t}
          side="dest"
          form={form}
          isSiteMode={routeMode === 'to_site'}
          logisticsSiteOptions={logisticsSiteOptions}
          selectedSite={selectedSite}
          selectedSiteId={selectedSiteId}
          setSelectedSiteId={setSelectedSiteId}
          locations={locations}
          selectedLocationId={destId}
          onSelectLocation={(loc) => selectLocation(loc, 'dest')}
          frequent={frequent}
          defaultLocationId={prefs.quoteDefaultDestLocationId ?? null}
          onSetDefault={(id) => saveDefault('dest', id)}
        />
      </div>
    </>
  );
}

/** One endpoint (origin or destination) of the route: either the logistics
 *  site picker (site mode) or the location / country / department fields. */
function EndpointFields({
  t,
  side,
  form,
  isSiteMode,
  logisticsSiteOptions,
  selectedSite,
  selectedSiteId,
  setSelectedSiteId,
  locations,
  selectedLocationId,
  onSelectLocation,
  frequent,
  defaultLocationId,
  onSetDefault,
}: {
  t: TFunction;
  side: 'origin' | 'dest';
  form: UseFormReturn<QuoteFormValues>;
  isSiteMode: boolean;
  logisticsSiteOptions: { value: string; label: string }[];
  selectedSite: ICarrier | null;
  selectedSiteId: string | null;
  setSelectedSiteId: (id: string | null) => void;
  locations: ILocation[];
  selectedLocationId: string | null;
  onSelectLocation: (loc: ILocation | null) => void;
  /** Destinations les plus servies depuis l'origine — côté destination seul. */
  frequent?: IFrequentDestination[];
  defaultLocationId: string | null;
  onSetDefault: (locationId: string | null) => void;
}) {
  const { setValue, watch, formState: { errors } } = form;
  const countryField = side === 'origin' ? ('originCountry' as const) : ('destCountry' as const);
  const departmentField = side === 'origin' ? ('originDepartment' as const) : ('destDepartment' as const);
  const departmentError = side === 'origin'
    ? errors.originDepartment?.message
    : errors.destDepartment?.message;
  const country = watch(countryField) || 'FRA';
  const isFrance = country === 'FRA';
  const postalCode = watch(side === 'origin' ? 'originPostalCode' : 'destPostalCode');

  // Hors France, le « département » est la région NUTS3 du pays (toute
  // l'Europe) : la liste vient du référentiel, pas de la liste française.
  const { data: countryDepartments = [] } = useCountryDepartments(country, !isSiteMode);
  const departmentOptions = isFrance ? DEPARTMENTS : countryDepartments;

  // Le code postal français fait foi : on en déduit le département. Changer de
  // pays vide la valeur : un département d'un pays n'a pas de sens dans un autre.
  const previousCountry = useRef(country);
  useEffect(() => {
    if (isSiteMode) return;
    if (previousCountry.current !== country) {
      previousCountry.current = country;
      if (form.getValues(departmentField)) setValue(departmentField, '', { shouldValidate: true });
      // Passage vers la France avec un CP déjà saisi : on le redérive tout de
      // suite plutôt que de laisser le département vide.
      if (country !== 'FRA') return;
    }
    if (!isFrance) return;
    const dep = departementDepuisCP(postalCode);
    if (dep && DEPARTMENTS.some((d) => d.value === dep)) {
      setValue(departmentField, dep, { shouldValidate: true });
    }
    // Déclenché par le code postal et le pays seulement : un département choisi
    // à la main reste tel quel tant que le code postal ne change pas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCode, country, isSiteMode]);

  const title = isSiteMode
    ? (side === 'origin' ? t('quote.departure') : t('quote.arrival'))
    : (side === 'origin' ? t('quote.origin') : t('quote.destination'));

  return (
    <div className="space-y-2">
      <p className="text-2xs font-semibold uppercase tracking-widest text-subtle">
        {title}
      </p>
      {isSiteMode ? (
        <div className="space-y-2">
          <Select
            aria-label={title}
            options={logisticsSiteOptions}
            placeholder={t('quote.selectSite')}
            value={selectedSiteId || ''}
            onChange={(e) => setSelectedSiteId(e.target.value || null)}
          />
          {selectedSite ? (
            <p className="text-2xs text-subtle flex items-center gap-1">
              <Warehouse className="h-3 w-3 text-warning" />
              {selectedSite.sitePostalCode} - {DEPARTMENTS.find((d) => d.value === selectedSite.siteDepartment)?.label || selectedSite.siteDepartment}
            </p>
          ) : null}
        </div>
      ) : (
        <>
          {locations.length > 0 && (
            <LocationPicker
              label={t('quote.fromLocation')}
              placeholder={t('quote.selectLocation')}
              locations={locations}
              value={selectedLocationId}
              onChange={onSelectLocation}
              frequent={frequent}
              defaultLocationId={defaultLocationId}
              onSetDefault={onSetDefault}
            />
          )}
          <Select
            label={t('quote.country')}
            options={COUNTRIES}
            value={country}
            onChange={(e) => setValue(countryField, e.target.value, { shouldValidate: true })}
          />
          <Select
            label={t('quote.department')}
            options={departmentOptions}
            placeholder={t('quote.selectPlaceholder')}
            error={departmentError}
            value={watch(departmentField)}
            onChange={(e) => setValue(departmentField, e.target.value, { shouldValidate: true })}
          />
        </>
      )}
    </div>
  );
}
